import type { SorterResult } from "antd/lib/table/interface";
import { type FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import type { SiteList } from "src/app/features/Site/types/SiteList";
import { type ExtendedColumnsType, GenericTable, type ResourceTableProps } from "../../../../../components";
import {
    displayDateTimeValue,
    displayOrgUnitUpdateStatusValue,
} from "../../../../../components/Table/components/PropertyTable/transformations/cellTransformations";
import { ResourceName } from "../../../../../core/router/config/ResourceName";
import { BASE_PATH, FORM_ACTION } from "../../../../../core/router/types/routerTypes";

export const SitesTable: FC<ResourceTableProps<SiteList>> = ({ onStateChanged, onRowSelectionChange, datasource, resource }) => {
    const { t } = useTranslation();
    const getColumnsConfig = useCallback(
        (sortInfo: SorterResult<SiteList>): ExtendedColumnsType<SiteList>[] => {
            return [
                {
                    title: t("site.column.id"),
                    key: "id",
                    dataIndex: "id",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "id" ? sortInfo.order : null,
                },
                {
                    title: t("site.column.name"),
                    key: "name",
                    dataIndex: "name",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "name" ? sortInfo.order : null,
                },
                {
                    title: t("site.column.siteId"),
                    key: "siteId",
                    dataIndex: "siteId",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "siteId" ? sortInfo.order : null,
                },
                {
                    title: t("site.column.parentUnit"),
                    key: "parentUnitName",
                    dataIndex: "parentUnitName",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "parentUnitName" ? sortInfo.order : null,
                },
                {
                    title: t("site.column.dealerName"),
                    key: "dealerName",
                    dataIndex: "dealerName",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "dealerName" ? sortInfo.order : null,
                },
                {
                    title: t("site.column.partnerId"),
                    key: "partnerId",
                    dataIndex: "partnerId",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "partnerId" ? sortInfo.order : null,
                },
                {
                    title: t("site.column.status"),
                    key: "status",
                    dataIndex: "status",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "status" ? sortInfo.order : null,
                    render: (value) => displayOrgUnitUpdateStatusValue(value),
                },
                {
                    title: t("site.column.changed"),
                    dataIndex: "changed",
                    key: "changed",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "changed" ? sortInfo.order : null,
                    render: (value) => displayDateTimeValue(value),
                },
            ];
        },
        [t],
    );

    const navigate = useNavigate();
    const onRowClick = (record: SiteList) => navigate(`${BASE_PATH}/${ResourceName.SITES}/${FORM_ACTION.EDIT}/${record.id}`);

    return (
        (datasource && (
            <GenericTable<SiteList>
                getColumnsConfig={getColumnsConfig}
                datasource={datasource}
                name="sites_table"
                onSelectionCallback={onRowSelectionChange}
                onStateChanged={onStateChanged}
                onRow={(record: SiteList) => {
                    return {
                        onClick: () => onRowClick(record),
                    };
                }}
                resource={resource}
            />
        )) ||
        null
    );
};
